import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Users, Calendar, FileText, Settings, Package } from 'lucide-react';
import axios from 'axios';
import './App.css';
import StundenerfassungView from './StundenerfassungView';
import SettingsView from './SettingsView';
import KalenderView from './KalenderView';
import BaustellenView from './BaustellenView';
import MitarbeiterView from './MitarbeiterView';
import SubunternehmerView from './SubunternehmerView';
import RechnungenView from './RechnungenView';
import MaterialerfassungView from './MaterialerfassungView';

const API_BASE_URL = 'https://baustellenmgmt.mas123.de/api';

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [appSettings, setAppSettings] = useState({ company_name: 'Baustellen-Verwaltung', logo: null, favicon: null, primary_color: '#0ea5e9' });

  const loadAppSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/app-settings/`);
      setAppSettings(response.data);
    } catch (error) {
      console.error('App-Settings laden fehlgeschlagen:', error);
    }
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/users/me/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('User laden fehlgeschlagen:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setToken(null);
      }
    }
  }, [token]);

  useEffect(() => {
    loadAppSettings();
  }, [loadAppSettings]);

  useEffect(() => {
    if (token) {
      loadUser();
    }
  }, [token, loadUser]);

  useEffect(() => {
    if (appSettings?.company_name) {
      document.title = appSettings.company_name;
    }
    if (appSettings?.primary_color) {
      document.documentElement.style.setProperty('--app-primary', appSettings.primary_color);
      const hex = appSettings.primary_color.replace('#', '');
      if (hex.length === 6) {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        if (!Number.isNaN(r) && !Number.isNaN(g) && !Number.isNaN(b)) {
          document.documentElement.style.setProperty('--app-primary-rgb', `${r}, ${g}, ${b}`);
        }
      }
    }
    if (appSettings?.favicon) {
      const href = appSettings.favicon;
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'icon');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    }
  }, [appSettings]);

  async function login(username, password) {
    try {
      const response = await axios.post(`${API_BASE_URL}/token/`, {
        username,
        password
      });

      const newToken = response.data.access;
      localStorage.setItem('token', newToken);
      setToken(newToken);
    } catch (error) {
      alert('Login fehlgeschlagen!');
    }
  }

  function logout() {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }

  if (!token) {
    return <LoginForm onLogin={login} appSettings={appSettings} />;
  }

  return (
    <div style={{minHeight: '100vh', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'}}>
      <nav style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
          {appSettings?.logo ? (
            <img src={appSettings.logo} alt="Logo" style={{ height: '32px', width: 'auto' }} />
          ) : (
            <Building2 size={32} color={appSettings?.primary_color || '#0ea5e9'} />
          )}
          <h1 style={{fontSize: '1.5rem', fontWeight: 'bold', margin: 0}}>
            {appSettings?.company_name || 'Baustellen-Verwaltung'}
          </h1>
        </div>

        <div style={{display: 'flex', gap: '1rem', alignItems: 'center'}}>
          <span>{user?.first_name} {user?.last_name}</span>
          <button onClick={logout} className="btn">Logout</button>
        </div>
      </nav>

      <div style={{ padding: '0 2rem 2rem' }}>
        <div style={{
          background: 'white',
          borderRadius: '0.75rem',
          border: '1px solid #e5e7eb',
          padding: '0.75rem 1rem',
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          marginTop: '1.5rem'
        }}>
            <MenuItem
              icon={<Building2 size={18} />}
              text="Dashboard"
              active={activeMenu === 'dashboard'}
              onClick={() => setActiveMenu('dashboard')}
              primaryColor={appSettings?.primary_color}
            />
          <MenuItem
            icon={<Building2 size={18} />}
            text="Baustellen"
            active={activeMenu === 'baustellen'}
            onClick={() => setActiveMenu('baustellen')}
            primaryColor={appSettings?.primary_color}
          />
          <MenuItem
            icon={<Users size={18} />}
            text="Mitarbeiter"
            active={activeMenu === 'mitarbeiter'}
            onClick={() => setActiveMenu('mitarbeiter')}
            primaryColor={appSettings?.primary_color}
          />
          <MenuItem
            icon={<Users size={18} />}
            text="Subunternehmer"
            active={activeMenu === 'subunternehmer'}
            onClick={() => setActiveMenu('subunternehmer')}
            primaryColor={appSettings?.primary_color}
          />
          <MenuItem
            icon={<Calendar size={18} />}
            text="Kalender"
            active={activeMenu === 'kalender'}
            onClick={() => setActiveMenu('kalender')}
            primaryColor={appSettings?.primary_color}
          />
          <MenuItem
            icon={<FileText size={18} />}
            text="Stundenerfassung"
            active={activeMenu === 'stunden'}
            onClick={() => setActiveMenu('stunden')}
            primaryColor={appSettings?.primary_color}
          />
          <MenuItem
            icon={<Settings size={18} />}
            text="Einstellungen"
            active={activeMenu === 'settings'}
            onClick={() => setActiveMenu('settings')}
            primaryColor={appSettings?.primary_color}
          />
          <MenuItem
            icon={<FileText size={18} />}
            text="Rechnungen"
            active={activeMenu === 'rechnungen'}
            onClick={() => setActiveMenu('rechnungen')}
            primaryColor={appSettings?.primary_color}
          />
          <MenuItem
            icon={<Package size={18} />}
            text="Materialerfassung"
            active={activeMenu === 'material'}
            onClick={() => setActiveMenu('material')}
            primaryColor={appSettings?.primary_color}
          />
        </div>

        <main style={{ padding: '2rem 0 0' }}>
          {activeMenu === 'stunden' ? (
            <StundenerfassungView token={token} />
          ) : activeMenu === 'mitarbeiter' ? (
            <MitarbeiterView token={token} />
          ) : activeMenu === 'subunternehmer' ? (
            <SubunternehmerView token={token} />
          ) : activeMenu === 'baustellen' ? (
            <BaustellenView token={token} />
          ) : activeMenu === 'rechnungen' ? (
            <RechnungenView token={token} />
          ) : activeMenu === 'material' ? (
            <MaterialerfassungView token={token} />
          ) : activeMenu === 'settings' ? (
            <SettingsView token={token} />
          ) : activeMenu === 'kalender' ? (
            <KalenderView token={token} />
          ) : (
            <div className="card">
              <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem'}}>
                {activeMenu === 'dashboard' && 'Willkommen im Baustellen-Management-System'}
                {activeMenu === 'baustellen' && 'Baustellen'}
                {activeMenu === 'mitarbeiter' && 'Mitarbeiter'}
              </h2>
              <p style={{color: '#6b7280'}}>
                Dies ist die Verwaltungsanwendung für das Baustellen-Management-System.
                Hier können Sie Baustellen verwalten, Mitarbeiter organisieren, und vieles mehr.
              </p>
              {activeMenu === 'dashboard' && (
                <>
                  <div style={{marginTop: '2rem', padding: '1rem', background: '#eff6ff', borderRadius: '0.5rem'}}>
                    <p style={{fontWeight: '600', color: '#1e40af'}}>
                      📌 Hinweis: Diese Anwendung ist getrennt von der Stundenzettel-App
                    </p>
                    <p style={{fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem'}}>
                      Einfache Mitarbeiter nutzen die Stundenzettel-App unter /stundenzettel
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function MenuItem({ icon, text, active, onClick, primaryColor }) {
  const primary = primaryColor || '#0ea5e9';
  return (
    <div
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.6rem 0.9rem',
        borderRadius: '999px',
        cursor: 'pointer',
        background: active ? '#eff6ff' : 'transparent',
        color: active ? primary : '#6b7280',
        fontWeight: active ? '600' : '400'
      }}
    >
      {icon}
      <span>{text}</span>
    </div>
  );
}

function LoginForm({ onLogin, appSettings }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const primary = appSettings?.primary_color || '#0ea5e9';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div className="card" style={{width: '100%', maxWidth: '400px'}}>
        <div style={{textAlign: 'center', marginBottom: '1.5rem'}}>
          {appSettings?.logo ? (
            <img src={appSettings.logo} alt="Logo" style={{ height: '48px', width: 'auto', margin: '0 auto 1rem' }} />
          ) : (
            <Building2 size={48} color={primary} style={{margin: '0 auto 1rem'}} />
          )}
          <h1 style={{fontSize: '1.5rem', fontWeight: 'bold', margin: 0}}>
            {appSettings?.company_name || 'Baustellen-Verwaltung'}
          </h1>
        </div>

        <input
          type="text"
          placeholder="Benutzername"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="input"
          style={{marginBottom: '1rem'}}
        />
        <input
          type="password"
          placeholder="Passwort"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="input"
          style={{marginBottom: '1rem'}}
          onKeyPress={(e) => e.key === 'Enter' && onLogin(username, password)}
        />
        <button
          onClick={() => onLogin(username, password)}
          className="btn btn-primary"
          style={{width: '100%'}}
        >
          Login
        </button>
      </div>
    </div>
  );
}

export default App;
